import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@app/../environments/environment';

/** One placeable element. Coordinates are twips; see label-units.ts. */
export interface LabelObject {
  id: string;
  type: 'text' | 'barcode' | 'image' | 'rect';
  frame: { x: number; y: number; w: number; h: number };
  style?: any;
  binding?: LabelBinding;
}

export type LabelBinding =
  | { kind: 'static'; text: string }
  | { kind: 'field'; field: string }
  | { kind: 'template'; text: string };

export interface LabelBody {
  schema: number;
  objects: LabelObject[];
}

export interface LabelTemplateSummary {
  tplID: number;
  name: string;
  kind: 'system' | 'custom';
  engine: string;
  subject: string;
  mediaId: string;
  mediaName: string;
  widthTwips: number;
  heightTwips: number;
  legacyKey: string | null;
  /** Labels per page for sheet stock; 0 for a roll that feeds a label printer. */
  perSheet: number;
  editable: boolean;
  updatedAt: string;
}

export interface LabelTemplateDetail extends LabelTemplateSummary {
  orientation: string;
  cornerRadiusTwips: number;
  version: number;
  body: LabelBody;
}

export interface LabelField {
  field: string;
  label: string;
  group: string;
  sample: string;
  isImage: boolean;
  readOnly: boolean;
}

/**
 * Where the labels sit on a page, for media that is sheet stock rather than a
 * roll. The numbers are the manufacturer's published geometry, and the PDF
 * compiler imposes the label on exactly this grid — so `columns * rows` is the
 * real "per sheet" figure, not an estimate.
 */
export interface LabelSheet {
  page: string;
  pageWidthTwips: number;
  pageHeightTwips: number;
  marginLeftTwips: number;
  marginTopTwips: number;
  pitchXTwips: number;
  pitchYTwips: number;
  columns: number;
  rows: number;
}

export interface LabelMedia {
  id: string;
  name: string;
  widthTwips: number;
  heightTwips: number;
  cornerRadiusTwips: number;
  engine: string;
  /* Present only on sheet stock. The catalogue endpoint has always sent it
     (LabelTemplateController spreads the whole media spec); it was missing
     here, so the designer could not tell a roll from a page of thirty. */
  sheet?: LabelSheet;
}

export interface LabelCatalogue {
  fields: LabelField[];
  media: LabelMedia[];
  symbologies: string[];
  types: string[];
}

/**
 * Label templates: the designer's data layer.
 *
 * Every write answers with the stored template, so callers replace what they
 * hold rather than patching it — the server owns the size (it comes from the
 * media) and the version.
 */
@Injectable({ providedIn: 'root' })
export class LabelTemplateService {
  private readonly endpoint = `${environment.apiUrl}/label-templates`;

  constructor(private http: HttpClient) {}

  list(params: { engine?: string; subject?: string } = {}): Observable<any> {
    /* Undefined entries are stripped, not passed through. HttpParams stringifies
       whatever it is given, so `{subject: undefined}` went out on the wire as
       the literal `subject=undefined` — which the server then filtered on, and
       every menu that asked for "any subject" came back empty. */
    const query: { [key: string]: string } = {};
    Object.keys(params).forEach((key) => {
      const value = (params as any)[key];
      if (value !== undefined && value !== null && value !== '') {
        query[key] = String(value);
      }
    });

    return this.http.get<any>(this.endpoint, { params: query });
  }

  /** Fields the designer may place, and the media it may target. */
  catalogue(): Observable<LabelCatalogue> {
    return this.http.get<LabelCatalogue>(`${this.endpoint}/catalogue`);
  }

  get(tplID: number): Observable<any> {
    return this.http.get<any>(`${this.endpoint}/${tplID}`);
  }

  create(payload: {
    tplName: string;
    tplMediaId: string;
    tplSubject?: string;
    tplBody?: LabelBody;
    locationOnly?: boolean;
  }): Observable<any> {
    return this.http.post<any>(this.endpoint, payload);
  }

  update(tplID: number, payload: { tplName?: string; tplBody?: LabelBody }): Observable<any> {
    return this.http.put<any>(`${this.endpoint}/${tplID}`, payload);
  }

  /** Starting point for a custom label: copy a system template and edit that. */
  /**
   * @param tplMediaId Copy onto different stock, scaling the objects to it.
   *   Omitted, the copy keeps the original's size. The server refuses media
   *   belonging to another printer type, and refuses a layout that cannot fit.
   */
  duplicate(tplID: number, tplName?: string, locationOnly = false, tplMediaId?: string): Observable<any> {
    return this.http.post<any>(`${this.endpoint}/${tplID}/duplicate`, { tplName, locationOnly, tplMediaId });
  }

  remove(tplID: number): Observable<any> {
    return this.http.delete<any>(`${this.endpoint}/${tplID}`);
  }

  /**
   * Compiles a template with real values into the vendor document the print
   * bridge hands to the printer. Values are passed explicitly rather than
   * through localStorage, which is what lets a designed label carry more than
   * the fixed dozen fields the old path allowed.
   */
  render(tplID: number, data: { [field: string]: string }): Observable<any> {
    return this.http.post<any>(`${this.endpoint}/${tplID}/render`, { data });
  }

  /**
   * A page of labels as a PDF, for an office printer.
   *
   * The server builds the field values from the product ids rather than taking
   * them from the caller: a sheet can carry any field in the catalogue, and the
   * list screen holds only the handful of columns it happens to show.
   *
   * @param startAt 1-based slot on the first sheet, so a part-used sheet can go
   *                back through the printer instead of being thrown away.
   */
  sheet(tplID: number, payload: {
    productIDs: number[];
    copies?: number;
    startAt?: number;
  }): Observable<Blob> {
    return this.http.post(`${this.endpoint}/${tplID}/sheet`, payload, {
      responseType: 'blob'
    });
  }
}
